import { Link } from '@tanstack/react-router';
import { ChevronRight } from 'lucide-react';
import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { AuditLogEntry } from '@nucleus-commerce/api-client/platform';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@nucleus-commerce/ui/components/table';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { Mono } from '@/shared/ui/mono';

export interface AuditLogTableProps {
  entries: readonly AuditLogEntry[];
  organizationNames: ReadonlyMap<string, string>;
  loading: boolean;
}

function changes(entry: AuditLogEntry): string {
  return JSON.stringify({ before: entry.before, after: entry.after }, null, 2);
}

/** One row per entry; expanding it shows the before/after fields, request id and IP. */
export function AuditLogTable({ entries, organizationNames, loading }: AuditLogTableProps) {
  const { t } = useTranslation('auditLog');
  const format = useFormat();
  const [open, setOpen] = useState<ReadonlySet<string>>(new Set());
  const toggle = (id: string) =>
    setOpen((previous) => {
      const next = new Set(previous);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <Table className="text-[13px]" aria-label={t('title')} aria-busy={loading}>
      <TableHeader>
        <TableRow className="hover:bg-transparent">
          <TableHead className="h-10 w-8 pl-5" />
          <TableHead className="h-10 text-xs font-medium">{t('columns.when')}</TableHead>
          <TableHead className="h-10 text-xs font-medium">{t('columns.actor')}</TableHead>
          <TableHead className="h-10 text-xs font-medium">{t('columns.action')}</TableHead>
          <TableHead className="h-10 text-xs font-medium">{t('columns.entity')}</TableHead>
          <TableHead className="h-10 pr-5 text-xs font-medium">
            {t('columns.organization')}
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {entries.length === 0 && !loading ? (
          <TableRow>
            <TableCell colSpan={6} className="text-muted-foreground h-20 text-center">
              {t('empty')}
            </TableCell>
          </TableRow>
        ) : (
          entries.map((entry, index) => {
            const expanded = open.has(entry.id);
            return (
              <Fragment key={entry.id}>
                <TableRow data-state={expanded ? 'open' : 'closed'}>
                  <TableCell className="py-2 pl-5">
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      aria-expanded={expanded}
                      data-tour={index === 0 ? 'platform.audit-log.row-details' : undefined}
                      aria-label={t('details', { action: entry.action })}
                      onClick={() => toggle(entry.id)}
                    >
                      <ChevronRight
                        aria-hidden
                        className={cn('transition-transform', expanded && 'rotate-90')}
                      />
                    </Button>
                  </TableCell>
                  <TableCell className="py-2 whitespace-nowrap" title={format.utc(entry.createdAt)}>
                    {format.dateTime(entry.createdAt)}
                  </TableCell>
                  <TableCell className="py-2">
                    <span className="flex flex-col">
                      <span>{entry.actor.name ?? t(`actorType.${entry.actor.type}`)}</span>
                      {entry.actor.name ? (
                        <span className="text-muted-foreground text-xs">
                          {t(`actorType.${entry.actor.type}`)}
                        </span>
                      ) : null}
                    </span>
                  </TableCell>
                  <TableCell className="py-2">
                    <Mono className="text-foreground">{entry.action}</Mono>
                  </TableCell>
                  <TableCell className="py-2">
                    <span className="flex flex-col">
                      <span>{entry.entityType}</span>
                      {entry.entityId ? <Mono className="text-xs">{entry.entityId}</Mono> : null}
                    </span>
                  </TableCell>
                  <TableCell className="py-2 pr-5">
                    {entry.organizationId ? (
                      <Link
                        to="/organizations/$organizationId"
                        params={{ organizationId: entry.organizationId }}
                        className="hover:underline"
                      >
                        {organizationNames.get(entry.organizationId) ?? entry.organizationId}
                      </Link>
                    ) : (
                      <span className="text-muted-foreground">{t('platformLevel')}</span>
                    )}
                  </TableCell>
                </TableRow>
                {expanded ? (
                  <TableRow className="hover:bg-transparent">
                    <TableCell colSpan={6} className="bg-muted/40 px-5 py-3">
                      <dl className="text-muted-foreground mb-2 flex flex-wrap gap-x-6 gap-y-1 text-xs">
                        <div className="flex gap-1">
                          <dt>{t('requestId')}</dt>
                          <dd>
                            <Mono>{entry.requestId ?? '—'}</Mono>
                          </dd>
                        </div>
                        <div className="flex gap-1">
                          <dt>{t('ip')}</dt>
                          <dd>
                            <Mono>{entry.ip ?? '—'}</Mono>
                          </dd>
                        </div>
                      </dl>
                      <pre className="bg-background overflow-x-auto rounded-md border p-3 text-xs">
                        {changes(entry)}
                      </pre>
                    </TableCell>
                  </TableRow>
                ) : null}
              </Fragment>
            );
          })
        )}
      </TableBody>
    </Table>
  );
}
